import { defineStore } from 'pinia'
import { useStoreAuth } from '@/stores/storeAuth'
import { supabase } from '@/supabase'

export const useStoreUsers = defineStore('storeUsers', {
  state: () => {
    return {
      items: [],
      loaded: false,
      loading: false,
      loadingMore: false,
      paginatedCursors: {},

      // Highlighted users pagination
      highlightedUsersPage: 1,
      highlightedUsersPageSize: 20,
      highlightedUsersTotalCount: 0,

      // Top users (server-ranked via the top_reviewers view)
      topUsers: [],
      topUsersLoaded: false,
      topUsersLoading: false,

      // Search related state
      searchResults: [],
      searchLoading: false,
      searchLoadingMore: false,
      searchError: null,
      searchQuery: '',
      searchTotalCount: 0,
      searchPage: 1,
      searchPageSize: 20
    }
  },

  actions: {
    init() {
      // this.fetchAllUsers()
    },

    updateUsers(fetchedItems) {
      const itemsMap = new Map(this.items.map((item) => [item.id, item]))

      fetchedItems.forEach((fetchedItem) => {
        if (itemsMap.has(fetchedItem.id)) {
          Object.assign(itemsMap.get(fetchedItem.id), fetchedItem)
        } else {
          itemsMap.set(fetchedItem.id, fetchedItem)
        }
      })

      this.items = Array.from(itemsMap.values())
    },

    // Top users for the home shelf, ranked server-side by the composite
    // get_top_users RPC (weighted reviews, titles seen, lists, invites,
    // followers, comments, active days). windowDays null = all-time.
    async fetchTopUsers(limit = 15, windowDays = 90) {
      this.topUsersLoading = true

      try {
        const { data, error } = await supabase.rpc('get_top_users', {
          p_window_days: windowDays,
          p_limit: limit
        })

        if (error) throw error

        this.topUsers = data || []
        this.topUsersLoaded = true
        return this.topUsers
      } catch (error) {
        console.error('Failed to fetch top users:', error.message)
        return []
      } finally {
        this.topUsersLoading = false
      }
    },

    async fetchHighlightedUsers(page = 1, pageSize = 20) {
      this.loading = true
      this.highlightedUsersPage = page
      this.highlightedUsersPageSize = pageSize

      try {
        // First, get the total count for pagination
        const { count, error: countError } = await supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true })

        if (countError) throw countError
        this.highlightedUsersTotalCount = count || 0

        // Calculate offset for pagination
        const offset = (page - 1) * pageSize

        // Get the users for the current page
        const { data, error } = await supabase
          .from('profiles')
          .select('*, reviews(count)')
          .eq('reviews.status', 'published')
          .eq('reviews.is_deleted', false)
          .order('created_at', { ascending: true })
          .range(offset, offset + pageSize - 1)

        if (error) throw error
        if (data) this.updateUsers(data)

        this.loaded = true
        return {
          users: data || [],
          totalCount: count || 0,
          totalPages: Math.ceil((count || 0) / pageSize)
        }
      } catch (error) {
        console.error('Failed to fetch highlighted users:', error.message)
      } finally {
        this.loading = false
      }
    },

    // Load more highlighted users (append to existing)
    async fetchMoreHighlightedUsers() {
      const totalPages = Math.ceil(
        this.highlightedUsersTotalCount / this.highlightedUsersPageSize
      )

      // Check if we have more pages
      if (this.highlightedUsersPage >= totalPages) {
        return
      }

      // Prevent duplicate requests
      if (this.loadingMore) {
        return
      }

      this.loadingMore = true

      try {
        const nextPage = this.highlightedUsersPage + 1
        const offset = (nextPage - 1) * this.highlightedUsersPageSize

        const { data, error } = await supabase
          .from('profiles')
          .select('*, reviews(count)')
          .eq('reviews.status', 'published')
          .eq('reviews.is_deleted', false)
          .order('created_at', { ascending: true })
          .range(offset, offset + this.highlightedUsersPageSize - 1)

        if (error) throw error

        // Append new users to existing items
        if (data) {
          this.updateUsers(data)
        }

        this.highlightedUsersPage = nextPage
      } catch (error) {
        console.error('Failed to load more highlighted users:', error.message)
      } finally {
        this.loadingMore = false
      }
    },

    async fetchSuggestedUsers() {
      // Default some usernames
      try {
        const isProduction = import.meta.env.VITE_APP_MODE === 'production'

        if (isProduction) {
          const defaultUsers = ['jbrads', 'niall', 'lulu']
          const { data, error } = await supabase
            .from('profiles')
            .select('*, reviews(count)')
            .eq('reviews.status', 'published')
            .eq('reviews.is_deleted', false)
            .neq('user_id', useStoreAuth().user.id)
            .in('username', defaultUsers)
            .limit(10)

          if (error) throw error
          if (data) return data
        } else {
          const { data, error } = await supabase
            .from('profiles')
            .select('*, reviews(count)')
            .eq('reviews.status', 'published')
            .eq('reviews.is_deleted', false)
            .neq('user_id', useStoreAuth().user.id)
            .limit(5)

          if (error) throw error
          if (data) return data
        }

        return []
      } catch (error) {
        console.error('Failed to fetch suggested users:', error.message)
        return []
      }
    },

    // Search users (resets results for new query)
    async searchUsers(query, pageSize = 20) {
      this.searchLoading = true
      this.searchError = null
      this.searchQuery = query
      this.searchPage = 1
      this.searchPageSize = pageSize
      this.searchResults = [] // Reset results for new search

      try {
        // Get current user to exclude from results
        const currentUserId = useStoreAuth().user?.id

        // Get count for pagination
        const { count, error: countError } = await supabase
          .from('profiles')
          .select('*, reviews(count)', { count: 'exact', head: true })
          .neq('user_id', currentUserId)
          .or(`username.ilike.%${query}%,displayname.ilike.%${query}%`)

        if (countError) throw countError

        this.searchTotalCount = count || 0

        // Get first page of results
        const { data, error: searchError } = await supabase
          .from('profiles')
          .select('*, reviews(count)')
          .eq('reviews.status', 'published')
          .eq('reviews.is_deleted', false)
          .neq('user_id', currentUserId)
          .or(`username.ilike.%${query}%,displayname.ilike.%${query}%`)
          .order('username', { ascending: true })
          .range(0, pageSize - 1)

        if (searchError) throw searchError

        // Set search results
        this.searchResults = data || []

        return {
          results: data || [],
          totalCount: count || 0,
          totalPages: Math.ceil((count || 0) / pageSize)
        }
      } catch (err) {
        this.searchError = err.message
        console.error('User search failed:', query, err.message)
        throw err
      } finally {
        this.searchLoading = false
      }
    },

    // Load more search results (append to existing)
    async fetchMoreSearchUsers() {
      const totalPages = Math.ceil(
        this.searchTotalCount / this.searchPageSize
      )

      // Check if we have more pages
      if (this.searchPage >= totalPages) {
        return
      }

      // Prevent duplicate requests
      if (this.searchLoadingMore) {
        return
      }

      this.searchLoadingMore = true

      try {
        const currentUserId = useStoreAuth().user?.id
        const nextPage = this.searchPage + 1
        const offset = (nextPage - 1) * this.searchPageSize

        const { data, error } = await supabase
          .from('profiles')
          .select('*, reviews(count)')
          .eq('reviews.status', 'published')
          .eq('reviews.is_deleted', false)
          .neq('user_id', currentUserId)
          .or(
            `username.ilike.%${this.searchQuery}%,displayname.ilike.%${this.searchQuery}%`
          )
          .order('username', { ascending: true })
          .range(offset, offset + this.searchPageSize - 1)

        if (error) throw error

        // Append new results to existing
        if (data) {
          this.searchResults = [...this.searchResults, ...data]
        }

        this.searchPage = nextPage
      } catch (error) {
        console.error('Failed to load more search results:', error.message)
      } finally {
        this.searchLoadingMore = false
      }
    },

    // Reset search state
    resetSearch() {
      this.searchResults = []
      this.searchQuery = ''
      this.searchTotalCount = 0
      this.searchPage = 1
      this.searchError = null
    },

    // Get user details by ID
    async getUserById(userId) {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*, reviews(count)')
          .eq('reviews.status', 'published')
          .eq('reviews.is_deleted', false)
          .eq('id', userId)
          .single()

        if (error) throw error

        // Update the store with this user
        if (data) {
          this.updateUsers([data])
        }

        return data
      } catch (err) {
        console.error('Failed to fetch user profile:', userId, err.message)
        throw err
      }
    }
  },

  getters: {
    highlightedUsers(state) {
      return state.items.filter((user) => true) // user.highlighted)
    },

    // Pagination getters for highlighted users
    highlightedUsersTotalPages(state) {
      return Math.ceil(
        state.highlightedUsersTotalCount / state.highlightedUsersPageSize
      )
    },

    hasMoreHighlightedUsers(state) {
      const totalPages = Math.ceil(
        state.highlightedUsersTotalCount / state.highlightedUsersPageSize
      )
      return state.highlightedUsersPage < totalPages
    },

    // Search functionality getters
    searchTotalPages(state) {
      return Math.ceil(state.searchTotalCount / state.searchPageSize)
    },

    hasMoreSearchUsers(state) {
      const totalPages = Math.ceil(
        state.searchTotalCount / state.searchPageSize
      )
      return state.searchPage < totalPages
    },

    hasSearchResults(state) {
      return state.searchResults.length > 0
    },

    isSearching(state) {
      return state.searchLoading
    }
  }
})
